import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Bed, Calendar, ArrowRight, Check, Loader2, Home, Heart, KeyRound } from "lucide-react";
import { FadeUp, ClipReveal } from "@/components/motion/Reveal";
import { MapSingle } from "@/components/Map";

function money(v, cur = "GBP") {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [saved, setSaved] = useState(false);
  const [savingWish, setSavingWish] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setP(data);
      } catch {
        navigate("/projects");
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (!user || !p) return;
    (async () => {
      try {
        const { data } = await api.get("/wishlist");
        setSaved((data.projects || []).some((x) => x.id === p.id));
      } catch {}
    })();
  }, [user, p]);

  const toggleWish = async () => {
    if (!user) { toast.info("Sign in to save projects."); navigate("/login"); return; }
    setSavingWish(true);
    try {
      if (saved) {
        await api.delete(`/wishlist/${p.id}`);
        setSaved(false);
        toast.success("Removed from your saved list.");
      } else {
        await api.post("/wishlist", { project_id: p.id });
        setSaved(true);
        toast.success("Saved to your list.");
      }
    } catch {
      toast.error("Could not update your list.");
    } finally { setSavingWish(false); }
  };

  if (!p) {
    return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500"><Loader2 className="animate-spin" /></div>;
  }

  const deposit = Math.round(p.price_from * 0.15);
  const financed = p.price_from - deposit;
  const monthly = Math.round((financed * (1 + 0.025 * 10)) / (10 * 12));
  const gallery = p.gallery && p.gallery.length ? p.gallery : [p.hero_image];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="project-detail-page">
      <button onClick={() => navigate("/projects")} className="text-[13px] mv-green-text hover:underline">← All projects</button>

      {/* Header */}
      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ {p.type}</span></div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text mt-2 leading-[1.05]">{p.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[14px]" style={{ color: "var(--mv-muted)" }}>
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} style={{ color: "var(--mv-gold)" }}/> {p.city}, {p.country}</span>
            <span className="inline-flex items-center gap-1.5"><Bed size={14} style={{ color: "var(--mv-gold)" }}/> {p.bedrooms} bedrooms</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} style={{ color: "var(--mv-gold)" }}/> Handover {p.handover}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[11.5px] px-3 py-1.5 rounded-full font-semibold" style={{ background: "rgba(212,175,55,0.2)", color: "#8a6b12" }}>{p.status}</span>
          <span className="text-[11.5px] px-3 py-1.5 rounded-full font-semibold" style={{ background: "rgba(10,59,40,0.08)", color: "var(--mv-green)" }}>{p.units_available} of {p.units_total} available</span>
        </div>
      </div>

      {/* Gallery */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ClipReveal className="lg:col-span-2">
          <div className="relative aspect-[16/10] overflow-hidden rounded-3xl">
            <motion.img key={active} initial={{ scale: 1.05, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7 }} src={gallery[active]} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </ClipReveal>
        <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
          {gallery.slice(0, 3).map((src, i) => (
            <button key={i} onClick={() => setActive(i)} className={`relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden rounded-2xl ${active === i ? "ring-2 ring-[color:var(--mv-gold)]" : ""}`} data-testid={`gallery-thumb-${i}`}>
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <FadeUp>
            <h2 className="font-display text-2xl md:text-3xl mv-green-text">About this project</h2>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--mv-muted)" }}>{p.description}</p>
          </FadeUp>

          <div className="mt-10">
            <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Highlights</span></div>
            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {(p.highlights || []).map((h, i) => (
                <FadeUp key={h} delay={i * 0.05}>
                  <li className="flex items-start gap-2 text-[14.5px]">
                    <Check size={18} className="mt-0.5" style={{ color: "var(--mv-gold)" }} /> {h}
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>

          {/* Location map */}
          {typeof p.lat === "number" && typeof p.lng === "number" && (
            <div className="mt-10" data-testid="project-detail-map">
              <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Location</span></div>
              <div className="flex items-center gap-2 mt-2 mb-4 text-[14px]" style={{ color: "var(--mv-muted)" }}>
                <MapPin size={14} style={{ color: "var(--mv-gold)" }}/> {p.address || `${p.city}, ${p.country}`}
              </div>
              <MapSingle lat={p.lat} lng={p.lng} label={p.name} height={360} zoom={13} />
            </div>
          )}
        </div>

        {/* Sticky-ish sidebar */}
        <FadeUp delay={0.1}>
          <div className="mv-card p-6 lg:sticky lg:top-24">
            <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>From</div>
            <div className="font-display text-[42px] mv-green-text leading-none tracking-tight">{money(p.price_from, p.currency)}</div>

            <div className="mt-6 space-y-3 text-[13.5px]">
              <div className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Deposit (15%)</span><span className="mv-green-text font-semibold">{money(deposit, p.currency)}</span></div>
              <div className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Financed (illustrative)</span><span className="mv-green-text font-semibold">{money(financed, p.currency)}</span></div>
              <div className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Est. monthly</span><span className="mv-green-text font-semibold">≈ {money(monthly, p.currency)}</span></div>
            </div>

            <div className="mt-6 space-y-3">
              <Link to={`/reserve/${p.id}`} className="btn-gold w-full justify-center" data-testid="project-detail-reserve">
                <KeyRound size={16}/> Reserve this plot (14 days, free)
              </Link>
              <Link to="/pay?type=deposit" className="btn-green w-full justify-center" data-testid="project-detail-pay">
                <Home size={16}/> Pay Deposit
              </Link>
              <Link to={`/site-visits?project=${p.id}`} className="btn-ghost w-full justify-center" data-testid="project-detail-visit">
                <MapPin size={16}/> Book a site visit
              </Link>
              <button
                type="button"
                onClick={toggleWish}
                disabled={savingWish}
                aria-label={saved ? "Remove from saved projects" : "Save this project"}
                className={`inline-flex items-center justify-center gap-2 w-full px-4 py-2 rounded-full text-[13.5px] border mv-hairline transition ${saved ? "bg-[color:var(--mv-gold)]/15 mv-green-text" : "hover:bg-black/[0.04]"}`}
                data-testid="project-detail-wishlist"
              >
                <Heart size={15} fill={saved ? "currentColor" : "none"}/>
                {saved ? "Saved" : "Save for later"}
              </button>

              {/* Investor protection badge */}
              <div className="mt-2 p-3 rounded-xl border mv-hairline text-[12px] leading-relaxed"
                   style={{ background: "rgba(212,175,55,0.10)", color: "var(--mv-ink)" }}
                   data-testid="project-detail-protection-badge">
                <div className="flex items-center gap-1.5 mb-1">
                  <Check size={13} style={{ color: "#0A7A2C" }}/>
                  <strong>14-day cooling off</strong>
                </div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Check size={13} style={{ color: "#0A7A2C" }}/>
                  <strong>MAVULA buy-back option</strong>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check size={13} style={{ color: "#0A7A2C" }}/>
                  <span>Independent escrow · title in your name</span>
                </div>
                <Link to="/protections" className="mt-2 inline-block underline mv-green-text text-[12px]" data-testid="project-detail-protection-link">
                  See all protections →
                </Link>
              </div>
            </div>

            <div className="mt-4 text-[12px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
              Pathway to Settlement eligible — enjoy relaxed residency for you & your family.
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
